// Story 11.2 — Shell base reusable para todos los nodos custom de Reactflow.
// Cada NodeXxx lo wrapea con sus colores e iconos.

import { Handle, Position } from '@xyflow/react';
import { cn } from '@/lib/utils';

interface Props {
  title: string;
  detail?: string;
  icon?: React.ReactNode;
  /** Tailwind class para el background (e.g. `bg-info/15`). */
  bgClass?: string;
  /** Tailwind class para el border (e.g. `border-info/40`). */
  borderClass?: string;
  /** Tailwind class para el texto (e.g. `text-info`). */
  textClass?: string;
  /** Si true, el shell usa clip-path diamond (para ConditionNode). */
  diamond?: boolean;
  /** True para ghost externo (estilo dashed). */
  ghost?: boolean;
  /** Story 11.3 — true cuando el cursor del editor cae en este step. */
  highlighted?: boolean;
}

export function NodeShell({
  title,
  detail,
  icon,
  bgClass,
  borderClass,
  textClass,
  diamond,
  ghost,
  highlighted,
}: Props): React.ReactElement {
  // P-366 (review 11.2): el wrapper SE MANTIENE axis-aligned para que los
  // `<Handle>` de Reactflow queden en top/bottom reales. La forma diamante
  // se logra con un layer interno rotado (background visible) + clip-path
  // — Reactflow ancla los edges al contenedor padre, no al inner rotado.
  return (
    <div
      className={cn(
        'relative min-w-[180px] max-w-[200px] rounded-md border px-3 py-2 text-xs shadow-sm',
        // El nodo diamante es transparente afuera; el "rombo" lo dibuja el
        // layer absoluto rotado de abajo.
        diamond ? 'border-transparent bg-transparent' : (bgClass ?? 'bg-card'),
        diamond ? '' : (borderClass ?? 'border-border'),
        textClass ?? 'text-foreground',
        ghost && 'border-dashed opacity-80',
        // Story 11.3 — outline brillante cuando el cursor del editor está
        // en este step. Usa `outline` (no `border`) para no chocar con
        // borderClass de cada variante.
        highlighted && 'outline outline-2 outline-offset-2 outline-accent',
      )}
    >
      {diamond && (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-1 -z-10 rotate-45 rounded-sm border',
            bgClass ?? 'bg-card',
            borderClass ?? 'border-border',
          )}
        />
      )}
      <Handle type="target" position={Position.Top} className="!bg-border" />
      <div className="flex items-center gap-1.5">
        {icon && <span className="shrink-0">{icon}</span>}
        <span className="truncate font-semibold">{title}</span>
      </div>
      {detail && <p className="mt-0.5 line-clamp-2 text-[10px] opacity-80">{detail}</p>}
      <Handle type="source" position={Position.Bottom} className="!bg-border" />
    </div>
  );
}
